/**
 * Centralized lazy and static page component definitions for the app shell.
 * Kept separate from route composition to keep `index.js` and area bundles small.
 */
import React from 'react';
import lazyRetry from '../utils/lazyRetry';

// =========================================================================
// LOADING FALLBACK
// =========================================================================
/**
 * Global Suspense fallback used when a route chunk is still downloading.
 *
 * Why this needs to be visible (R29 in ARCHITECTURE.md):
 * Some route chunks (e.g. FieldPlanningPage) are large and the browser may
 * spend hundreds of ms parsing them after a click. If the fallback is a tiny
 * 50vh centered spinner, users on top of an already-rendered page perceive
 * the click as ignored and click again — which can re-trigger effects.
 *
 * Layout: a fullscreen, semi-transparent backdrop + centered spinner +
 * a 3px indeterminate top progress bar so the click feedback is instant.
 * Keyframes are injected once, lazily, to avoid a global CSS dependency.
 */
const TOP_BAR_KEYFRAMES_ID = 'archon-route-progress-keyframes';

const ensureRouteProgressKeyframes = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TOP_BAR_KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = TOP_BAR_KEYFRAMES_ID;
  style.textContent = `
@keyframes archonRouteProgress {
  0%   { transform: translateX(-100%); }
  50%  { transform: translateX(20%); }
  100% { transform: translateX(100%); }
}`;
  document.head.appendChild(style);
};

export const LoadingFallback = ({ label = 'Loading…' } = {}) => {
  ensureRouteProgressKeyframes();
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1080,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(15, 23, 42, 0.35)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        pointerEvents: 'none',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          overflow: 'hidden',
          backgroundColor: 'rgba(248, 162, 51, 0.18)',
        }}
      >
        <div
          style={{
            width: '40%',
            height: '100%',
            backgroundColor: 'var(--archon-accent)',
            animation: 'archonRouteProgress 1.1s ease-in-out infinite',
          }}
        />
      </div>
      <div className="spinner-border text-primary" role="presentation" />
      <div
        style={{
          marginTop: 12,
          color: '#fff',
          fontSize: '0.95rem',
          fontWeight: 500,
          textShadow: '0 1px 2px rgba(0,0,0,0.45)',
        }}
      >
        {label}
      </div>
    </div>
  );
};

// =========================================================================
// STATIC IMPORTS (Critical path)
// =========================================================================
export { default as LandingPage } from '../components/landing/LandingPage';
export { default as LoginForm } from '../components/auth/LoginForm';
export { default as ComingSoon } from '../components/ui/feedback/ComingSoon';

// =========================================================================
// LAZY - Projects
// =========================================================================
export const ProjectList = lazyRetry(() => import('../components/projects/list/ProjectList'));
export const ProjectForm = lazyRetry(() => import('../components/projects/ProjectForm'));
export const ProjectScopeManager = lazyRetry(() => import('../components/projects/scope/ProjectScopeManager'));
export const ProjectSchedule = lazyRetry(() => import('../components/projects/schedule/ProjectSchedule'));
export const PreplotPage = lazyRetry(() => import('../components/projects/preplot/PreplotPage'));
export const CombinedProgressMap = lazyRetry(() => import('../components/projects/progress/CombinedProgressMap'));
export const ProjectDashboard = lazyRetry(() => import('../components/projects/dashboard/ProjectDashboard'));
export const ApprovedTenderPage = lazyRetry(() => import('../components/projects/tender/ApprovedTenderPage'));

// =========================================================================
// LAZY - Tender Management (pre-project bid workspaces)
// =========================================================================
export const TenderListPage = lazyRetry(() => import('../components/tenders/TenderListPage'));
export const TenderRoomPage = lazyRetry(() => import('../components/tenders/TenderRoomPage'));
export const TenderLedgerPage = lazyRetry(() => import('../components/tenders/TenderLedgerPage'));

// =========================================================================
// LAZY - Finance & budget
// =========================================================================
export const BudgetDashboard = lazyRetry(() => import('../components/finance/BudgetDashboard'));
export const COAManager = lazyRetry(() => import('../components/finance/coa').then(m => ({ default: m.COAManager })));
export const ExchangeRatesManager = lazyRetry(() => import('../components/finance/exchange-rates/ExchangeRatesManager'));
export const BudgetManager = lazyRetry(() => import('../components/budget/common/BudgetManager'));
export const BudgetMngLog = lazyRetry(() => import('../components/budget/management/BudgetMngLog'));
export const BudgetSurv = lazyRetry(() => import('../components/budget/survey/BudgetSurv'));
export const BudgetDril = lazyRetry(() => import('../components/budget/drilling/BudgetDril'));
export const BudgetReco = lazyRetry(() => import('../components/budget/recording/BudgetReco'));

// =========================================================================
// LAZY - Petty cash
// =========================================================================
export const PettyCashDashboard = lazyRetry(() => import('../components/finance/pettycash').then(m => ({ default: m.PettyCashDashboard })));
export const NewPettyCash = lazyRetry(() => import('../components/finance/pettycash').then(m => ({ default: m.NewPettyCash })));
export const PettyCashFunds = lazyRetry(() => import('../components/finance/pettycash').then(m => ({ default: m.PettyCashFunds })));
export const PettyCashReports = lazyRetry(() => import('../components/finance/pettycash').then(m => ({ default: m.PettyCashReports })));
export const PettyCashFundDetail = lazyRetry(() => import('../components/finance/pettycash').then(m => ({ default: m.PettyCashFundDetail })));
export const PettyCashMonthlyReport = lazyRetry(() => import('../components/finance/pettycash').then(m => ({ default: m.PettyCashMonthlyReport })));

// =========================================================================
// LAZY - Corporate expenses
// =========================================================================
export const CorporateExpensesList = lazyRetry(() => import('../components/finance/corporate').then(m => ({ default: m.CorporateExpensesList })));
export const NewCorporateExpense = lazyRetry(() => import('../components/finance/corporate').then(m => ({ default: m.NewCorporateExpense })));
export const CompanyAssetsList = lazyRetry(() => import('../components/finance/corporate').then(m => ({ default: m.CompanyAssetsList })));
export const CompanyAssetNew = lazyRetry(() => import('../components/finance/corporate').then(m => ({ default: m.CompanyAssetNew })));
export const CompanyAssetDetail = lazyRetry(() => import('../components/finance/corporate').then(m => ({ default: m.CompanyAssetDetail })));

// =========================================================================
// LAZY - Consolidated expenses
// =========================================================================
export const ConsolidatedExpenseDashboard = lazyRetry(() => import('../components/finance/consolidated/ConsolidatedExpenseDashboard'));

// =========================================================================
// LAZY - Dailies
// =========================================================================
export const DailySurv = lazyRetry(() => import('../components/daily/survey/DailySurv'));
export const SurveyMapPage = lazyRetry(() => import('../components/survey/map/SurveyMapPage'));
export const SurveyPlanningPage = lazyRetry(() => import('../components/survey/planning/SurveyPlanningPage'));
export const SurveyProductionReportsPage = lazyRetry(() => import('../components/survey/production/SurveyProductionReportsPage'));
export const DailyDril = lazyRetry(() => import('../components/daily/drilling/DailyDril'));
export const DailyReco = lazyRetry(() => import('../components/daily/recording/DailyReco'));
export const RecordingMapPage = lazyRetry(() => import('../components/recording/map/RecordingMapPage'));
export const RecordingPlanningPage = lazyRetry(() => import('../components/recording/planning/RecordingPlanningPage'));
export const RecordingProductionReportsPage = lazyRetry(() => import('../components/recording/production/RecordingProductionReportsPage'));
export const DailyMngLog = lazyRetry(() => import('../components/daily/management/DailyMngLog'));
export const DailiesManagement = lazyRetry(() => import('../components/daily/DailiesManagement'));
export const DailySummaryReport = lazyRetry(() => import('../components/daily/report/DailySummaryReport'));

// =========================================================================
// LAZY - Master budget
// =========================================================================
export const MasterBudget = lazyRetry(() => import('../components/master/MasterBudget'));

// =========================================================================
// LAZY - Platform admin
// =========================================================================
export const CreateTenantForm = lazyRetry(() => import('../components/platform/CreateTenantForm'));
export const TenantList = lazyRetry(() => import('../components/platform/TenantList'));
export const TenantAnalytics = lazyRetry(() => import('../components/platform/TenantAnalytics'));
export const DemoInvitationManager = lazyRetry(() => import('../components/platform/DemoInvitationManager'));
export const DemoUsersManager = lazyRetry(() => import('../components/platform/DemoUsersManager'));
export const DemoRequestsManager = lazyRetry(() => import('../components/platform/DemoRequestsManager'));
export const AccessDeveloperHub = lazyRetry(() => import('../components/platform/AccessDeveloperHub'));
export const AdministrationLandingPage = lazyRetry(() => import('../components/administration/AdministrationLandingPage'));

// =========================================================================
// LAZY - Configuration / admin
// =========================================================================
export const ClientManager = lazyRetry(() => import('../components/configuration/ClientManager'));
export const DepartmentManager = lazyRetry(() => import('../components/configuration/DepartmentManager'));
export const RoleManager = lazyRetry(() => import('../components/configuration/RoleManager'));
export const EquipmentCatManager = lazyRetry(() => import('../components/configuration/EquipmentCatManager'));
export const EquipmentItemManager = lazyRetry(() => import('../components/configuration/EquipmentItemManager'));
export const UserManager = lazyRetry(() => import('../components/admin/UserManager'));
export const AccessCtrlMgr = lazyRetry(() => import('../components/admin/AccessCtrlMgr'));
export const BackupsPage = lazyRetry(() => import('../components/admin/backups/BackupsPage'));
export const CompanyBackupPage = lazyRetry(() => import('../components/company/CompanyBackupPage'));

// =========================================================================
// LAZY - HR
// =========================================================================
export const CandidateList = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CandidateList })));
export const CandidateForm = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CandidateForm })));
export const CandidateDetail = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CandidateDetail })));
export const BlacklistManager = lazyRetry(() => import('../components/hr').then(m => ({ default: m.BlacklistManager })));
export const HSEInductionManager = lazyRetry(() => import('../components/hr').then(m => ({ default: m.HSEInductionManager })));
export const HRConfiguration = lazyRetry(() => import('../components/hr').then(m => ({ default: m.HRConfiguration })));
export const PersonnelAddForm = lazyRetry(() => import('../components/hr').then(m => ({ default: m.PersonnelAddForm })));
export const PersonnelTimesheet = lazyRetry(() => import('../components/hr').then(m => ({ default: m.PersonnelTimesheet })));
export const TimesheetCodeConfig = lazyRetry(() => import('../components/hr').then(m => ({ default: m.TimesheetCodeConfig })));
export const TimesheetEntries = lazyRetry(() => import('../components/hr').then(m => ({ default: m.TimesheetEntries })));
export const CompanyPayroll = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CompanyPayroll })));
export const CorporatePayroll = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CorporatePayroll })));
export const ProjectPayroll = lazyRetry(() => import('../components/hr').then(m => ({ default: m.ProjectPayroll })));
export const CompanyPersonnelDetail = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CompanyPersonnelDetail })));
export const CompanyPersonnelForm = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CompanyPersonnelForm })));
export const CompanyTimesheet = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CompanyTimesheet })));
export const CompanyTimesheetEntries = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CompanyTimesheetEntries })));
export const CompanyEmployeesList = lazyRetry(() => import('../components/hr').then(m => ({ default: m.CompanyEmployeesList })));
export const KeyPersonnelList = lazyRetry(() => import('../components/hr').then(m => ({ default: m.KeyPersonnelList })));

// ETAPA 5a — las dos pantallas unificadas: Personnel y Timesheet.
export const PersonnelList = lazyRetry(() => import('../components/hr/PersonnelList'));
export const TimesheetMonth = lazyRetry(() => import('../components/hr/TimesheetMonth'));
export const AvailablePersonnelList = lazyRetry(() => import('../components/hr').then(m => ({ default: m.AvailablePersonnelList })));
export const ProjectTeamList = lazyRetry(() => import('../components/hr').then(m => ({ default: m.ProjectTeamList })));
export const AssignmentHistoryView = lazyRetry(() => import('../components/hr').then(m => ({ default: m.AssignmentHistoryView })));
export const WorkforceStatistics = lazyRetry(() => import('../components/hr').then(m => ({ default: m.WorkforceStatistics })));
export const ProjectTimesheetView = lazyRetry(() => import('../components/hr').then(m => ({ default: m.ProjectTimesheetView })));
export const MonthlyReports = lazyRetry(() => import('../components/hr').then(m => ({ default: m.MonthlyReports })));

// =========================================================================
// LAZY - Inventory
// =========================================================================
export const InventoryDashboard = lazyRetry(() => import('../components/inventory/InventoryDashboard'));
export const InventoryMobileHub = lazyRetry(() => import('../components/inventory/InventoryMobileHub'));
export const InventoryMobileAddItem = lazyRetry(() => import('../components/inventory/InventoryMobileAddItem'));
export const InventoryItemForm = lazyRetry(() => import('../components/inventory/InventoryItemForm'));
export const InventoryItemDetail = lazyRetry(() => import('../components/inventory/InventoryItemDetail'));
export const InventoryParameters = lazyRetry(() => import('../components/inventory/InventoryParameters'));
export const VendorManager = lazyRetry(() => import('../components/inventory/vendors/VendorManager'));
export const KitManager = lazyRetry(() => import('../components/inventory/kits/KitManager'));

// =========================================================================
// LAZY - Procurement
// =========================================================================
export const RequisitionList = lazyRetry(() => import('../components/procurement').then(m => ({ default: m.RequisitionList })));
export const RequisitionForm = lazyRetry(() => import('../components/procurement').then(m => ({ default: m.RequisitionForm })));
export const RequisitionDetail = lazyRetry(() => import('../components/procurement').then(m => ({ default: m.RequisitionDetail })));
export const PurchaseOrderList = lazyRetry(() => import('../components/procurement').then(m => ({ default: m.PurchaseOrderList })));
export const PurchaseOrderForm = lazyRetry(() => import('../components/procurement').then(m => ({ default: m.PurchaseOrderForm })));
export const PurchaseOrderDetail = lazyRetry(() => import('../components/procurement').then(m => ({ default: m.PurchaseOrderDetail })));
export const ProcurementApprovalInbox = lazyRetry(() => import('../components/procurement').then(m => ({ default: m.ProcurementApprovalInbox })));

// =========================================================================
// LAZY - Catering (food cost estimation)
// =========================================================================
export const CateringHub = lazyRetry(() => import('../components/catering/CateringHub'));
export const CateringEstimator = lazyRetry(() => import('../components/catering/CateringEstimator'));
export const FoodItemManager = lazyRetry(() => import('../components/catering/FoodItemManager'));
export const RationTemplateManager = lazyRetry(() => import('../components/catering/RationTemplateManager'));
export const SavedEstimatesList = lazyRetry(() => import('../components/catering/SavedEstimatesList'));

// =========================================================================
// LAZY - QHSE
// =========================================================================
export const QHSEMainPage = lazyRetry(() => import('../components/qhse/QHSEMainPage'));
export const DocumentControlPage = lazyRetry(() => import('../components/qhse/documents/DocumentControlPage'));

// =========================================================================
// LAZY - Operations & drilling ops
// =========================================================================
export const HelicopterOps = lazyRetry(() => import('../components/operations/helicopter').then(m => ({ default: m.HelicopterOps })));
export const HelicopterConfigList = lazyRetry(() => import('../components/operations/helicopter').then(m => ({ default: m.HelicopterConfigList })));
export const HelicopterRegistry = lazyRetry(() => import('../components/operations/helicopter').then(m => ({ default: m.HelicopterRegistry })));
export const AssetRegistry = lazyRetry(() => import('../components/operations/fleet/AssetRegistry'));
export const AssetDetail = lazyRetry(() => import('../components/operations/fleet/AssetDetail'));
export const ProjectFleet = lazyRetry(() => import('../components/operations/fleet/ProjectFleet'));
export const FleetCategoryManager = lazyRetry(() => import('../components/operations/fleet/FleetCategoryManager'));
export const DailyLogPage = lazyRetry(() => import('../components/operations/fleet/DailyLogPage'));
export const MaintenanceLog = lazyRetry(() => import('../components/operations/fleet/MaintenanceLog'));
export const FieldOperations = lazyRetry(() => import('../components/operations/field-tracking/FieldOperations'));
export const FieldPlanningPage = lazyRetry(() => import('../components/operations/field-tracking/FieldPlanningPage'));
export const OperationsChat = lazyRetry(() => import('../components/operations/chat/OperationsChat'));
export const CampLayout = lazyRetry(() => import('../components/camp/CampLayout'));
export const FuelControlMain = lazyRetry(() => import('../components/operations/fuel-control/FuelControlMain'));
export const SupervisorFuelCard = lazyRetry(() => import('../components/operations/fuel-control/SupervisorFuelCard'));
export const JmMain = lazyRetry(() => import('../components/operations/jm/JmMain'));
export const JmRegisterPage = lazyRetry(() => import('../components/operations/jm/JmRegisterPage'));
export const JmSettingsPage = lazyRetry(() =>
  import('../components/operations/jm/JmMain').then((m) => ({ default: m.JmSettingsPage })),
);
export const SPRegistryPage = lazyRetry(() => import('../components/drilling/sp-registry/SPRegistryPage'));
export const DrillingOperationsDashboardPage = lazyRetry(() =>
  import('../components/drilling/dashboard/DrillingOperationsDashboardPage'),
);
export const MapView = lazyRetry(() => import('../components/drilling/sp-registry/MapView'));
export const CrewReportsPage = lazyRetry(() => import('../components/drilling/crew-reports/CrewReportsPage'));
export const CrewReportForm = lazyRetry(() => import('../components/drilling/crew-reports/CrewReportForm'));
export const CrewReportFieldPage = lazyRetry(() => import('../components/drilling/crew-reports/field/CrewReportFieldPage'));
export const LostTimePage = lazyRetry(() => import('../components/drilling/npt-log/LostTimePage'));
export const ClientDailyReportPage = lazyRetry(() => import('../components/drilling/client-daily-report/ClientDailyReportPage'));
export const WorkProjectionPage = lazyRetry(() => import('../components/drilling/work-projection/WorkProjectionPage'));
export const CDRArchivePage = lazyRetry(() => import('../components/drilling/client-daily-report/CDRArchivePage'));
export const NPTReportArchivePage = lazyRetry(() => import('../components/drilling/npt-log/NPTReportArchivePage'));

// =========================================================================
// LAZY - Settings / help / app home
// =========================================================================
export const CompanySettings = lazyRetry(() => import('../components/settings/CompanySettings'));
export const CurrencyModule = lazyRetry(() => import('../components/settings/CurrencyModule'));
export const DisplaySettingsPage = lazyRetry(() => import('../components/settings/DisplaySettingsPage'));
export const MyProfile = lazyRetry(() => import('../components/settings/MyProfile'));
export const UserManual = lazyRetry(() => import('../components/help').then(m => ({ default: m.UserManual })));
export const FAQPage = lazyRetry(() => import('../components/help').then(m => ({ default: m.FAQPage })));
export const AppHome = lazyRetry(() => import('../components/AppHome'));
